import React from 'react';
import { observer } from 'mobx-react-lite';
import { Formik } from 'formik';
import * as Yup from 'yup';

import { handleFormikParallelSubmit } from 'src/helpers';
import { useStore } from '@src/models';
import { Modal, Text, TextBox, BaseLink, StyledButton, Delimiter } from 'src/kit';
import { BaseFormikForm, FormikFileUploader, FormikSubmitButton } from '@src/features/common';

type Props = {
  onClose: () => void;
  onSubmit: () => void;
};

export const allowedResumeExtensions = ['pdf'];

const validationSchema = Yup.object({
  cv: Yup.mixed()
    .oneOfFileExtension(allowedResumeExtensions)
    .required()
});

export const OnboardingModal = observer(({ onClose, onSubmit }: Props) => {
  const { authStore, jobsStore } = useStore();

  React.useEffect(() => {
    // if signed in through linkedin
    if (authStore.isAuthorized) {
      onSubmit();
    }
  }, [onSubmit, authStore.isAuthorized]);

  const handleSubmit = async (values, actions) => {
    const requests = jobsStore.postAndParseCV({ payload: values.cv });
    await handleFormikParallelSubmit({ requests, actions });
    onSubmit();
  };

  return (
    <Modal onClose={onClose} closeOnClickOutside={false} boxShadow="shadow1">
      <TextBox mb={40} color="textSecondary" fontSize={25} lineHeight={1.4} textAlign="center">
        We now just need some basic information in order to go forward
      </TextBox>
      <Formik initialValues={{ cv: null }} validationSchema={validationSchema} onSubmit={handleSubmit}>
        {({ isSubmitting }) => (
          <BaseFormikForm withExitConfirmation={false}>
            <FormikFileUploader
              name="cv"
              text="Drag and drop your resume/CV here"
              allowedExtensions={allowedResumeExtensions}
              withIcon={false}
              mb={20}
            />
            <FormikSubmitButton size="md" mb={30} width={1}>
              {isSubmitting ? 'Uploading...' : 'Upload your resume/CV'}
            </FormikSubmitButton>
            <Delimiter mb={30} />
            <StyledButton onClick={onSubmit} variant="caInverted" size="md" mb={30} width={1} colors="textSecondary">
              Fill out my information manually
            </StyledButton>
            <Text mb={20} color="coolGrey" fontSize={16} textAlign="center">
              This action will sign you up. By signing up, you agree with our&nbsp;
              <BaseLink
                href="/Terms and Conditions Candidates.pdf"
                openInNewTab
                display="inline-block"
                color="aquaBlue"
              >
                T&Cs
              </BaseLink>
            </Text>
          </BaseFormikForm>
        )}
      </Formik>
    </Modal>
  );
});
